import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'App/styled';
import { hexToRgb } from '@nykaa/ui-components/styles/utils';
import SizeChart from '@nykaa/size-chart-desktop';
import { variantTypes, SIZE_CHART } from 'shared/constants';
import {
  selectSizeChartVariants,
  selectVariants,
  selectVariantType,
  selectCurrentSku,
} from 'shared/store/product/selector';
import { changeCurrentVariant } from 'shared/store/product/action';
import { State } from 'shared/store/types';
import ForwardArrow from 'shared/components/Icons/ForwardArrow';
import { handleSizeChart } from './utils';
import ActionWrapper from 'components/CardAction';
import { isRegisteredViaMobile } from 'shared/store/auth/helper';
import { ProductRenderProps } from 'types/product';
import Shades from './Shades';
import Sizes from './Sizes';
import DropdownHeader from './DropdownHeader';
import { handleClickSizeSelect } from './helper';
import { redirectAuthPage } from 'helpers/auth';
import { PageSource } from 'constants/dataLayer';
import { useSnsEnabled } from 'hooks/sns';

const Wrap = styled.div`
  ${({ theme }) => theme.typography.bodyMedium};
  padding: ${({ theme: { spacing } }) =>
    `${spacing.spacing20} ${spacing.spacing0} ${spacing.spacing0}`};
  color: ${({ theme }) => hexToRgb(theme.colors.textPrimary, 0.92)};
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  max-width: 504px;
  .text {
    text-transform: capitalize;
  }
`;

const AllSize = styled.button`
  ${({ theme }) => theme.typography.subTitleMedium};
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
  margin-right: ${({ theme }) => theme.spacing.spacing20};
  &:focus {
    outline: none;
  }
  svg {
    margin-left: ${({ theme }) => theme.spacing.spacing20};
    vertical-align: middle;
    path {
      fill: ${({ theme }) => theme.colors.primary};
    }
  }
`;

const AllShades = styled.button`
  ${({ theme }) => theme.typography.subTitleMedium};
  border: none;
  background: transparent;
  border-bottom: 1px solid ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.black100};
  cursor: pointer;
  margin: ${({ theme }) => theme.spacing.spacing0};
  height: 30px;
  &:focus {
    outline: none;
  }
`;
const Title = styled.div`
  margin-top: ${({ theme }) => theme.spacing.spacing20};
  ${({ theme }) => theme.typography.bodyLarge};
  text-transform: capitalize;
`;

const Variants = () => {
  const variants = useSelector((state: State) => selectVariants(state));
  const currentSku = useSelector((state: State) => selectCurrentSku(state));
  const user = useSelector((state: State) => state.auth?.user);
  const variantType = useSelector((state: State) => selectVariantType(state));
  const { childId } = currentSku || {};
  const [selectedOption, setSelectedOption] = useState(childId || '');
  const [isDisableLazy, setDisableLazy] = useState(false);
  const dispatch = useDispatch();
  const product = useSelector((state: State) => state.productPage.product);
  const isSnsEnabled = useSnsEnabled();
  const sizeChartVariants = useSelector((state: State) =>
    selectSizeChartVariants(state)
  );
  const productSizeData = product?.sizeData;
  const {
    enabled = false,
    units = [],
    columns = [],
    unitText = '',
  } = productSizeData || {};
  if (!variants || variants.length < 0 || !childId || !variantType) {
    return null;
  }
  const variantList = variants.map((variant) => ({
    id: variant.childId,
    name: variant.variantName,
  }));
  const changeVariant = (id: string) => {
    setSelectedOption(id);
    dispatch(changeCurrentVariant(id));
  };
  return (
    <Wrap>
      {enabled && (
        <ActionWrapper enableSecondaryTagTracking>
          {({
            handleAddToCart,
            handleSliderAction,
            handleNotifyMe,
            handleWishlist,
          }: ProductRenderProps) => (
            <SizeChart
              title="Size Chart"
              options={sizeChartVariants}
              units={units}
              sizeOptions={columns}
              handleClickSizeSelect={(id) =>
                handleClickSizeSelect({ id, setSelectedOption, dispatch })
              }
              unitSelectionText={unitText}
              selectedOption={selectedOption}
              // unSelectVariantOncloseChart={() => unSelectVariant({ setSelectedOption })} // dont know why this is added
              handleSliderAction={handleSliderAction}
              addToCartCallback={handleAddToCart}
              wishlistCallback={handleWishlist}
              sendNotifyMe={handleNotifyMe}
              user={user}
              redirectAuthPage={redirectAuthPage}
              isRegisteredViaMobile={isRegisteredViaMobile}
              pageLocation={PageSource.Product}
              isPro={user?.isPro}
              product={currentSku}
            />
          )}
        </ActionWrapper>
      )}
      <Header>
        {isSnsEnabled ? (
          <Title>Select {variantType?.toLowerCase()}</Title>
        ) : (
          <DropdownHeader
            variantList={variantList}
            title={variantType}
            currentProductId={childId}
            setDisableLazy={setDisableLazy}
          />
        )}
        {enabled && (
          <AllSize
            type="button"
            onClick={() => handleSizeChart(columns, currentSku)}
          >
            {SIZE_CHART}
            <ForwardArrow />
          </AllSize>
        )}
        {variantType === variantTypes.SHADE && (
          <AllShades type="button">
            {`All Shades (${variants.length})`}
          </AllShades>
        )}
      </Header>
      {variantType === variantTypes.SHADE && (
        <Shades
          changeVariant={changeVariant}
          currentVariantId={childId}
          variants={variants}
          setDisableLazy={setDisableLazy}
          isDisableLazy={isDisableLazy}
        />
      )}
      {variantType === variantTypes.SIZE && (
        <Sizes
          changeVariant={(id: string) => changeVariant(id)}
          currentVariantId={childId}
          variants={variants}
        />
      )}
    </Wrap>
  );
};

export default Variants;
